import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import { DiscrepancyService } from "@services/ingest/discrepancyService";
import { OrganizationService } from "@services/organizations/OrganizationService";
import type { Discrepancy } from "@models/Discrepancy.model";
import type { Organization } from "@models/organization.model";
import type { PopulationSegment } from "@models/populationSegment.model";
import { appStore } from "@store/appStore";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (discrepancy: Discrepancy) => void;
  /** Prefill, e.g. from the unmatched-rows Slack alert or a population page. A fixed
   * population hides the pickers. */
  populationSegmentId?: number | null;
  ingestRunId?: number | null;
  reason?: string;
}

/*
    Ops reports a discrepancy by hand, usually from a client's email (MPN-2009). It is the
    "why": every correction added later points back here.
*/
export default function ReportDiscrepancyDialog({
  open, onClose, onSaved, populationSegmentId, ingestRunId, reason: initialReason,
}: Props) {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgId, setOrgId] = useState<number | "">("");
  const [segments, setSegments] = useState<PopulationSegment[]>([]);
  const [segmentId, setSegmentId] = useState<number | "">(populationSegmentId ?? "");
  const [reason, setReason] = useState(initialReason ?? "");
  const [clientRequest, setClientRequest] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fixedPopulation = Boolean(populationSegmentId);

  useEffect(() => {
    if (!open || fixedPopulation) return;
    new OrganizationService().getOrganizations(appStore.loggedInUser.organization.id).then(setOrgs);
  }, [open, fixedPopulation]);

  useEffect(() => {
    setSegments([]);
    setSegmentId(populationSegmentId ?? "");
    if (orgId === "") return;
    new OrganizationService().getPopulationSegments(orgId).then(setSegments);
  }, [orgId, populationSegmentId]);

  const save = async () => {
    if (segmentId === "") return;
    setSaving(true);
    setError(null);
    try {
      const saved = await new DiscrepancyService().report({
        population_segment_id: segmentId,
        reason,
        client_request: clientRequest,
        ingest_run_id: ingestRunId ?? null,
      });
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Report a discrepancy</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        {error && <Alert severity="error">{error}</Alert>}
        {fixedPopulation ? (
          <Typography sx={{ fontSize: 13 }}>
            Population #{populationSegmentId}
            {ingestRunId ? ` · file #${ingestRunId}` : ""}
          </Typography>
        ) : (
          <>
            <TextField select size="small" label="Client" value={orgId}
              onChange={(e) => setOrgId(Number(e.target.value))}>
              {orgs.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Population" value={segmentId} disabled={orgId === ""}
              onChange={(e) => setSegmentId(Number(e.target.value))}>
              {segments.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
          </>
        )}
        <TextField size="small" label="What's wrong" required multiline minRows={3} value={reason}
          onChange={(e) => setReason(e.target.value)} />
        <TextField size="small" label="Client request (optional)" placeholder="Email, ticket or file link"
          value={clientRequest} onChange={(e) => setClientRequest(e.target.value)} />
        <Typography sx={{ fontSize: 12, color: "#64748b" }}>
          This is the "why". Every correction you add points back here, so the client's request
          travels with the change.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={save} sx={{ textTransform: "none" }}
          disabled={saving || segmentId === "" || !reason.trim()}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
